import { useState, useCallback } from 'react';

// Real API service for production
const AnalyticsAPI = {
  baseURL: 'https://gottea-search-5gtrn.ondigitalocean.app',
  
  async submitQuery(prompt) {
    console.log('🚀 Submitting query to API:', prompt);
    
    try {
      const requestBody = { prompt };
      console.log('📤 Request payload:', requestBody);
      
      const response = await fetch(`${this.baseURL}/ai/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
        // 35 second timeout to handle long-running queries
        signal: AbortSignal.timeout(60000)
      });

      console.log('📥 API Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ API Response data:', data);
      
      // Handle different possible response formats from the API
      let processedResponse;
      
      if (data.success === false) {
        throw new Error(data.error || data.message || 'Query failed');
      }
      
      // Flexible response parsing to handle different API response formats
      processedResponse = {
        success: true,
        prompt,
        generatedSQL: data.generatedSQL || data.sql || data.query || '',
        results: data.results || data.data || data.rows || [],
        rowCount: data.rowCount || data.count || (data.results?.length) || (data.data?.length) || 0,
        aiResponse: data.aiResponse || data.summary || data.explanation || data.description || ''
      };
      
      console.log('🔄 Processed response:', processedResponse);
      return processedResponse;
      
    } catch (error) {
      console.error('💥 Analytics API Error:', error);
      
      // Handle different types of errors with more specific messages
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        throw new Error('Query timeout: The request took too long to process. This might be due to complex data analysis. Try a simpler query or try again later.');
      }
      
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        throw new Error('Network error: Unable to connect to the analytics server. Please check your internet connection and try again.');
      }
      
      if (error.message.includes('HTTP 500')) {
        throw new Error('Server error: There was an internal server error while processing your request. Please try again in a moment.');
      }
      
      if (error.message.includes('HTTP 404')) {
        throw new Error('Service unavailable: The analytics endpoint is not available. Please contact support.');
      }
      
      if (error.message.includes('HTTP 400')) {
        throw new Error('Invalid query: The server could not understand your request. Please try rephrasing your question using simpler terms.');
      }
      
      if (error.message.includes('HTTP 401') || error.message.includes('HTTP 403')) {
        throw new Error('Access denied: You do not have permission to access the analytics service. Please contact support.');
      }
      
      // Re-throw the original error if it's already a formatted message
      throw error;
    }
  },

  // Fallback to mock data if API is unavailable (for development/demo)
  async generateFallbackResponse(prompt) {
    console.log('🔄 Using fallback response for:', prompt);
    
    const promptLower = prompt.toLowerCase();
    
    // Mock responses for different query types (same as before)
    if (promptLower.includes('top') && promptLower.includes('customer')) {
      return {
        success: true,
        prompt,
        generatedSQL: `
SELECT 
  customer_name,
  customer_phone,
  SUM(order_total) as total_spent
FROM orders o
JOIN customers c ON o.customer_id = c.id
WHERE o.created_at >= DATE_SUB(NOW(), INTERVAL 1 MONTH)
GROUP BY c.id
ORDER BY total_spent DESC
LIMIT 5;
        `.trim(),
        results: [
          { customer_name: "btc customer", customer_phone: "999999999", total_spent: 3794.00 },
          { customer_name: "avi null", customer_phone: "971795470", total_spent: 2970.00 },
          { customer_name: "Got Tea VK", customer_phone: "999999999", total_spent: 2950.00 },
          { customer_name: "NICK null", customer_phone: "881065749", total_spent: 2928.00 },
          { customer_name: "Samridh Anand", customer_phone: "880022277", total_spent: 2648.00 }
        ],
        rowCount: 5,
        aiResponse: "Here are the top 5 customers by spending this month: btc customer leads with $3,794, followed by avi null with $2,970, and Got Tea VK with $2,950. These loyal customers represent significant value to your business. (Note: This is demo data - API connection failed)"
      };
    }
    
    if (promptLower.includes('revenue') && promptLower.includes('month')) {
      return {
        success: true,
        prompt,
        generatedSQL: `
SELECT SUM(order_total) as monthly_revenue
FROM orders
WHERE created_at >= DATE_SUB(NOW(), INTERVAL 1 MONTH);
        `.trim(),
        results: [{ monthly_revenue: 47832.50 }],
        rowCount: 1,
        aiResponse: "Your total revenue for this month is $47,832.50, showing strong performance compared to previous months. (Note: This is demo data - API connection failed)"
      };
    }
    
    if (promptLower.includes('product') && (promptLower.includes('best') || promptLower.includes('top'))) {
      return {
        success: true,
        prompt,
        generatedSQL: `
SELECT 
  product_name,
  COUNT(*) as orders_count,
  SUM(quantity) as total_quantity,
  SUM(line_total) as total_revenue
FROM order_items oi
JOIN products p ON oi.product_id = p.id
JOIN orders o ON oi.order_id = o.id
WHERE o.created_at >= CURDATE()
GROUP BY p.id
ORDER BY orders_count DESC
LIMIT 10;
        `.trim(),
        results: [
          { product_name: "Brown Sugar Boba", orders_count: 45, total_quantity: 67, total_revenue: 1340.00 },
          { product_name: "Matcha Latte", orders_count: 38, total_quantity: 52, total_revenue: 1248.00 },
          { product_name: "Thai Tea", orders_count: 32, total_quantity: 41, total_revenue: 984.00 },
          { product_name: "Taro Milk Tea", orders_count: 28, total_quantity: 35, total_revenue: 875.00 },
          { product_name: "Classic Bubble Tea", orders_count: 25, total_quantity: 33, total_revenue: 742.50 }
        ],
        rowCount: 5,
        aiResponse: "Today's best-selling products are led by Brown Sugar Boba with 45 orders, followed by Matcha Latte with 38 orders. These popular items are driving significant revenue. (Note: This is demo data - API connection failed)"
      };
    }
    
    if (promptLower.includes('sales') && promptLower.includes('today')) {
      return {
        success: true,
        prompt,
        generatedSQL: `
SELECT 
  COUNT(*) as total_orders,
  SUM(order_total) as total_revenue,
  AVG(order_total) as avg_order_value,
  COUNT(DISTINCT customer_id) as unique_customers
FROM orders
WHERE DATE(created_at) = CURDATE();
        `.trim(),
        results: [{
          total_orders: 156,
          total_revenue: 3842.50,
          avg_order_value: 24.63,
          unique_customers: 134
        }],
        rowCount: 1,
        aiResponse: "Today's sales summary: 156 total orders generating $3,842.50 in revenue, with an average order value of $24.63 from 134 unique customers. (Note: This is demo data - API connection failed)"
      };
    }
    
    // Default response for unrecognized queries
    return {
      success: true,
      prompt,
      generatedSQL: `SELECT 'API Connection Failed' as status, 'Using fallback data' as message;`,
      results: [{ status: "API Connection Failed", message: "Using fallback demo data" }],
      rowCount: 1,
      aiResponse: "Unable to connect to the analytics API. This is demo data showing the interface capabilities. Please check your connection or contact support if the issue persists."
    };
  }
};

export const useAnalyticsAPI = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('processing');
  const [currentResults, setCurrentResults] = useState(null);
  const [error, setError] = useState(null);

  const submitQuery = useCallback(async (prompt) => {
    if (!prompt?.trim()) {
      setError('Please enter a query');
      return null;
    }

    console.log('🎯 Starting query submission:', prompt);
    setIsLoading(true);
    setError(null);
    setCurrentResults(null);

    try {
      // Phase 1: Processing (0-3s)
      setLoadingPhase('processing');
      console.log('📝 Phase 1: Processing query...');
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Phase 2: Generating SQL (3-8s)
      setLoadingPhase('generating');
      console.log('⚡ Phase 2: Generating SQL...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Phase 3: Fetching data (8-25s)
      setLoadingPhase('fetching');
      console.log('📊 Phase 3: Fetching data...');
      
      // Make the actual API call
      let response;
      try {
        response = await AnalyticsAPI.submitQuery(prompt);
        console.log('✅ API call successful');
      } catch (apiError) {
        console.warn('⚠️ Primary API failed, trying fallback:', apiError.message);
        
        // If it's a network error or endpoint not found, try fallback
        if (apiError.message.includes('Network error') || 
            apiError.message.includes('Failed to fetch') ||
            apiError.message.includes('Service unavailable')) {
          
          console.log('🔄 Using fallback data...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          response = await AnalyticsAPI.generateFallbackResponse(prompt);
        } else {
          // Re-throw other errors (timeout, server errors, etc.)
          throw apiError;
        }
      }

      // Phase 4: Formatting results (25-30s)
      setLoadingPhase('formatting');
      console.log('✨ Phase 4: Formatting results...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (response && response.success) {
        console.log('🎉 Query completed successfully');
        setCurrentResults(response);
        return response;
      } else {
        throw new Error(response?.error || 'Query failed - no data returned');
      }
    } catch (err) {
      console.error('❌ Analytics query failed:', err);
      setError(err);
      return null;
    } finally {
      setIsLoading(false);
      console.log('🏁 Query process completed');
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearResults = useCallback(() => {
    setCurrentResults(null);
    setError(null);
  }, []);

  return {
    submitQuery,
    isLoading,
    loadingPhase,
    currentResults,
    setCurrentResults,
    error,
    clearError,
    clearResults
  };
}; 